import { motion, AnimatePresence } from "framer-motion"
import { useState, useEffect } from "react"
import {Sparkles, Droplets, Zap, Shield, Heart, Calendar, MapPin, CheckCircle2, Users, Briefcase, Plane, Battery, ChevronDown, AlertCircle, Send, Building2, Dumbbell, Flower2, HelpCircle, Menu, X} from 'lucide-react'
import { Link } from "react-router-dom"
import { useHead, useSeoMeta } from "@unhead/react"
import HavenNav from "@/components/HavenNav"

const JANE_APP = 'https://havenintegrativehealth.janeapp.com/login'
const JANE_BOOK_NOW = 'https://havenintegrativehealth.janeapp.com/locations/haven-wellness-health-iv-therapy/book#/staff_member/1/treatment/10'
const INQUIRY_EMAIL = 'info@havenintegrativehealthconsulting.com'

const ivNavLinks = [
  { label: 'Home', href: '/' },
  { label: 'Our Drips', href: '#our-drips' },
  { label: 'Add-Ons', href: '#add-ons' },
  { label: 'Events', href: '#event-inquiry' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'FAQ', href: '#faq' },
]

function IVSubNav() {
  const [open, setOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <div className={`fixed top-[76px] left-0 right-0 z-40 transition-all duration-500 ${scrolled ? 'bg-[#1F1D1A]/95 backdrop-blur-md shadow-[0_1px_0_rgba(201,169,110,0.15)]' : 'bg-[#1F1D1A]/85 backdrop-blur-sm'}`}>
      <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-10 py-3 flex items-center justify-between">
        <a href="#iv-top" className="flex items-center gap-2.5 group">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#C9A96E] to-[#9B8E7E] flex items-center justify-center text-[#1F1D1A] text-[13px]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
            <Droplets className="w-4 h-4" />
          </div>
          <div className="leading-tight">
            <div className="text-[12.5px] tracking-[0.18em] uppercase text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Haven IV</div>
            <div className="text-[9px] tracking-[0.3em] uppercase text-[#C9A96E]">Nutrient Therapy</div>
          </div>
        </a>

        <div className="hidden lg:flex items-center gap-6">
          {ivNavLinks.map(l => (
            l.href.startsWith('#') ? (
              <a key={l.label} href={l.href} className="text-[11px] tracking-[0.18em] uppercase text-white/70 hover:text-[#C9A96E] transition-colors">
                {l.label}
              </a>
            ) : (
              <Link key={l.label} to={l.href} className="text-[11px] tracking-[0.18em] uppercase text-white/70 hover:text-[#C9A96E] transition-colors">
                {l.label}
              </Link>
            )
          ))}
          <a href={JANE_APP} target="_blank" rel="noopener noreferrer" className="ml-2 px-5 py-2 rounded-full bg-[#C9A96E] text-[#1F1D1A] text-[10.5px] tracking-[0.2em] uppercase font-semibold hover:bg-[#D4B57A] transition-all hover:shadow-[0_8px_24px_rgba(201,169,110,0.35)]">
            Book IV Therapy
          </a>
        </div>

        <button onClick={() => setOpen(!open)} className="lg:hidden w-9 h-9 flex items-center justify-center text-white" aria-label="Toggle IV menu">
          {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="lg:hidden overflow-hidden bg-[#1F1D1A] border-t border-[#C9A96E]/10">
            <div className="px-5 py-5 space-y-3">
              {ivNavLinks.map(l => (
                l.href.startsWith('#') ? (
                  <a key={l.label} href={l.href} onClick={() => setOpen(false)} className="block text-[12px] tracking-[0.2em] uppercase text-white/75 py-1">
                    {l.label}
                  </a>
                ) : (
                  <Link key={l.label} to={l.href} onClick={() => setOpen(false)} className="block text-[12px] tracking-[0.2em] uppercase text-white/75 py-1">
                    {l.label}
                  </Link>
                )
              ))}
              <a href={JANE_APP} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 px-5 py-2.5 rounded-full bg-[#C9A96E] text-[#1F1D1A] text-[11px] tracking-[0.2em] uppercase font-semibold">
                Book IV Therapy
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
}

const services = [
  {
    icon: Zap,
    title: "Energy & Vitality",
    description: "Support your natural vitality with targeted nutrient infusions designed to replenish B vitamins, magnesium, and key nutrients that fuel daily energy.",
  },
  {
    icon: Shield,
    title: "Everyday Wellness",
    description: "A nourishing blend of vitamins, minerals, and antioxidants to support your body's natural balance and day-to-day well-being.",
  },
  {
    icon: Droplets,
    title: "Hydration & Recovery",
    description: "Deep cellular hydration to help you recover from travel, workouts, and busy seasons of life.",
  },
  {
    icon: Sparkles,
    title: "Skin-Supportive Nutrients",
    description: "Nutritional IV blends featuring vitamin C, biotin, zinc, and selenium to support hydration and the nutrients your skin relies on from within.",
  },
]

const addOns = [
  { name: "Hangover Support", description: "Trace elements to help ease nausea and support rehydration", price: "+$50" },
  { name: "Glutathione", description: "A potent antioxidant that supports the body's natural detoxification pathways", price: "+$50" },
  { name: "Deeper Hydration", description: "Additional IV fluid volume for fuller hydration and cellular replenishment", price: "+$30" },
]



const eventOptions = [
  { value: "office", label: "Office / Corporate Wellness", icon: Building2 },
  { value: "fitness", label: "Fitness Studio", icon: Dumbbell },
  { value: "bachelorette", label: "Bachelorette Party", icon: Sparkles },
  { value: "bridal", label: "Bridal Glow with Friends", icon: Flower2 },
  { value: "other", label: "Other", icon: Heart },
]

const steps = [
  {
    number: "01",
    title: "Book your consultation",
    description: "Schedule online through our Jane App booking system in under 60 seconds.",
  },
  {
    number: "02",
    title: "Initial consultation & approval",
    description: "Begin with a personalized consultation with our pharmacist and nurse practitioner to ensure your infusion is safe, appropriate, and tailored to your wellness goals. Regulated care from the start.",
  },
  {
    number: "03",
    title: "We come to you",
    description: "Relax at home, your office, or your event venue. Our registered nurse, specialized in IV insertion and monitoring, administers your infusion with precision and care.",
  },
]

const drips = [
  {
    id: "premium",
    name: "Haven Wellness Premium Blend",
    tagline: "A Signature Wellness Infusion",
    price: "$300",
    image: "https://static.lumi.new/65/650a50459f7f2abd609530b39b788263.png",
    glowColor: "rgba(222, 208, 188, 0.75)",
    glowColor2: "rgba(245, 236, 220, 0.65)",
    accentText: "text-[#9B8E7E]",
    accentBorder: "border-[#E8E4DE]",
    accentBg: "bg-[#F5F0E8]",
    icon: Droplets,
    iconColor: "text-[#9B8E7E]",
    description: "Our signature infusion, a thoughtfully crafted blend of essential vitamins, minerals, and amino acids to support hydration, replenishment, and everyday vitality.",
    benefits: ["Nutritional replenishment", "Cellular hydration support", "Everyday wellness support", "Supports mental clarity & focus", "Antioxidant support"],
    ingredients: "Haven's Signature Myers' Cocktail: B-Complex · B12 · Magnesium · Calcium · Electrolytes",
    ideal: "A great fit for busy professionals, post-travel recovery, and anyone seeking nutritional replenishment and hydration.",
  },
  {
    id: "stress",
    name: "Haven Wellness Stress Support",
    tagline: "Calm. Replenish. Rebalance.",
    price: "$300",
    image: "https://static.lumi.new/5f/5f48e1371d5f0c0347be8030fcf391da.png",
    glowColor: "rgba(222, 208, 188, 0.75)",
    glowColor2: "rgba(245, 236, 220, 0.65)",
    accentText: "text-[#9B8E7E]",
    accentBorder: "border-[#E8E4DE]",
    accentBg: "bg-[#F5F0E8]",
    icon: Zap,
    iconColor: "text-[#9B8E7E]",
    description: "Formulated with Magnesium to support muscle relaxation, Taurine to support the nervous system, and our Signature Myers' Cocktail, a time-tested nutritional blend that supports balance and everyday well-being.",
    benefits: ["Nervous system support", "Supports muscle relaxation", "Supports mood & restful sleep", "Supports stress recovery", "Supports overall balance"],
    ingredients: "Myers' Cocktail · Taurine · High-dose Magnesium · B-Complex · Vitamin C · Calcium · Electrolytes",
    ideal: "Designed for high-stress lifestyles, busy seasons, and anyone seeking nutritional support for relaxation and balance.",
  },
  {
    id: "athletic",
    name: "Haven Wellness Athletic Support",
    tagline: "Recovery. Hydration. Replenishment.",
    price: "$300",
    image: "https://static.lumi.new/05/05ccd9b19e4fe979ed8fdc7ffe93fc94.png",
    glowColor: "rgba(222, 208, 188, 0.75)",
    glowColor2: "rgba(245, 236, 220, 0.65)",
    accentText: "text-[#9B8E7E]",
    accentBorder: "border-[#E8E4DE]",
    accentBg: "bg-[#F5F0E8]",
    icon: Battery,
    iconColor: "text-[#9B8E7E]",
    description: "Crafted for athletes and active individuals, this infusion supports muscle recovery, replenishes electrolytes lost during training, and provides nutritional support for your next workout.",
    benefits: ["Supports muscle recovery", "Electrolyte replenishment", "Supports post-workout recovery", "Supports endurance & stamina", "Hydration support"],
    ingredients: "Magnesium · Calcium · Amino Acids · B-Complex Vitamins · IV Fluids · Electrolytes",
    ideal: "A great fit for athletes, weekend warriors, post-training recovery, and pre-event preparation.",
  },
  {
    id: "glow",
    name: "Haven Wellness Skin-Supportive Blend",
    tagline: "Hydration. Nutrients. Well-being.",
    price: "$300",
    image: "https://static.lumi.new/18/18114ae73e790acbea765a037cebd29e.png",
    glowColor: "rgba(230, 210, 200, 0.75)",
    glowColor2: "rgba(245, 230, 220, 0.65)",
    accentText: "text-[#9B8E7E]",
    accentBorder: "border-[#E8E4DE]",
    accentBg: "bg-[#F5F0E8]",
    icon: Sparkles,
    iconColor: "text-[#9B8E7E]",
    description: "A nutritional IV blend featuring Vitamin C, Biotin, Zinc, and Selenium to support hydration and the nutrients your skin relies on from within.",
    benefits: ["Hydration support", "Antioxidant support", "Supports nutrient replenishment", "Supports hair & nail health", "Everyday wellness support"],
    ingredients: "Vitamin C · Biotin · Zinc · Selenium · IV Hydration Base",
    ideal: "A great fit for anyone looking to support their skin and overall wellness through nutrition and hydration.",
  },
  {
    id: "iron",
    name: "Haven IV Iron Therapy",
    tagline: "Monoferric or Venofer Iron Infusion",
    price: "$400",
    image: "https://static.lumi.new/6b/6bade99480e70b4a8b2be26a5a6318c2.png",
    glowColor: "rgba(120, 62, 30, 0.55)",
    glowColor2: "rgba(80, 40, 20, 0.45)",
    accentText: "text-[#8A4A26]",
    accentBorder: "border-[#D8B49A]",
    accentBg: "bg-[#F3E4D8]",
    icon: Droplets,
    iconColor: "text-[#8A4A26]",
    description: "A slow, carefully monitored Monoferric or Venofer iron infusion administered over 3.5 to 4 hours by a highly experienced and trained ICU registered nurse, who monitors your vitals closely and stays with you throughout the entire infusion.",
    benefits: ["Replenishes iron stores", "Supports energy levels", "Administered by an ICU-trained registered nurse", "Continuous vitals monitoring throughout", "Pharmacist-reviewed protocol", "Slow, low-risk infusion rate"],
    ingredients: "Monoferric or Venofer (Iron Infusion) · IV Fluids · Electrolytes",
    ideal: "A great fit for individuals with confirmed iron deficiency seeking a slow, medically supervised iron infusion with dedicated one-on-one nursing care.",
  },
]

const whoItsFor = [
  { icon: Briefcase, label: "Busy professionals", description: "Optimize performance without slowing down" },
  { icon: Heart, label: "Brides & events", description: "Glow from the inside out for your special day" },
  { icon: Plane, label: "Travel recovery", description: "Beat jet lag and arrive feeling refreshed" },
  { icon: Battery, label: "Fatigue & burnout", description: "Restore energy and reclaim your vitality" },
]

const faqs = [
  {
    q: "How does it work?",
    a: "Select your treatment and book your appointment online. Enter your appointment location and billing information. Complete your Health History form to be reviewed by our pharmacist and nurse practitioner. Our registered nurse arrives at the scheduled time, and you relax in the privacy of your home, office, or hotel while we deliver your infusion.",
  },
  {
    q: "How long will my IV treatment take?",
    a: "It varies depending on your selected infusion, but most IV treatments are completed within 30 to 60 minutes from the moment our nurse arrives and sets up to the end of your session.",
  },
  {
    q: "Will my insurance cover IV treatments?",
    a: "Many extended health plans may reimburse IV nutrient therapy on request. If you have a Health Spending Account (HSA) or Flexible Spending Account (FSA), you may be able to use these funds toward part or all of your infusion. On request, we can provide a detailed invoice you can submit to your insurance provider. Simply email our team after your appointment.",
  },
  {
    q: "Does insurance cover mobile IV therapy?",
    a: "Coverage for mobile IV therapy varies by insurance plan. Some health plans, HSAs, and FSAs may reimburse part of the cost, while others may not. Because of the wide range of providers and policy types, Haven does not verify insurance coverage on your behalf. We recommend contacting your insurance provider directly to confirm if wellness IV therapy, hydration treatments, or vitamin infusions are eligible under your plan. We provide detailed receipts on request so you can submit them for potential reimbursement. Tip: ask your insurer if they cover out-of-hospital IV hydration or nutrient therapy administered by a licensed nurse.",
  },
  {
    q: "What is in your IVs?",
    a: "Our IV solutions are formulated with a thoughtful blend of vitamins, minerals, amino acids, and antioxidants mixed in a balanced salt solution (normal saline). Every ingredient is sourced from reputable Canadian medical suppliers and licensed pharmacies, and every formulation is reviewed by our pharmacist and nurse practitioner before administration.",
  },
  {
    q: "Who can get an IV?",
    a: "Almost anyone. Patients with certain medical conditions may not be appropriate candidates for our treatments. After you book, our clinical team reviews your complete health history to ensure you are a safe fit for your selected infusion. IV therapy is generally safe for pregnant and breastfeeding patients; however, if you are pregnant we request that you consult with your obstetrician before your appointment.",
  },
  {
    q: "Are there any side effects?",
    a: "Mild sensations during treatment, such as a warm feeling, a taste of vitamins, or a brief head-rush-like sensation, can occur with certain ingredients. After your treatment, a small bruise may occasionally appear at the insertion site. Our registered nurse monitors you throughout the entire infusion to ensure your comfort.",
  },
  {
    q: "How often can I get an IV treatment?",
    a: "The ideal frequency depends on your health goals. Some clients benefit from weekly or bi-weekly infusions during busy or depleting seasons, while others prefer a maintenance boost every few months. Our clinical team will advise on the right rhythm for you during your consultation.",
  },
  {
    q: "Does it hurt?",
    a: "Our team is composed of licensed medical professionals with years of IV placement and infusion experience. We use a small pediatric-sized IV catheter, so at most you may feel a quick pinch. Pre-IV topical numbing cream is available on request to minimize any discomfort.",
  },
  {
    q: "Do you do private parties or offer group rates?",
    a: "Yes. We provide service for private parties (large or small) in almost any setting, from bridal and bachelorette events to corporate offices, fitness studios, and hotels. We offer special group rates for larger bookings. Submit your inquiry through the form on this page with your event size and details, and a member of our team will reach out to curate your experience.",
  },
]

function IVDripShowcase() {
  const [active, setActive] = useState(drips[0].id)
  const [expandedBenefit, setExpandedBenefit] = useState<string | null>(null)
  const current = drips.find(d => d.id === active)!

  return (
    <section id="our-drips" className="py-14 sm:py-20 lg:py-32 bg-[#FAF8F5] relative overflow-hidden">
      {/* Soft warm glow blobs */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-1/4 left-1/6 w-[700px] h-[700px] rounded-full blur-[180px] transition-all duration-1000"
          style={{ background: current.glowColor, opacity: 0.7 }}
        />
        <div
          className="absolute bottom-1/4 right-1/6 w-[600px] h-[600px] rounded-full blur-[160px] transition-all duration-1000"
          style={{ background: current.glowColor2, opacity: 0.6 }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full blur-[120px] transition-all duration-1000"
          style={{ background: "rgba(240, 228, 210, 0.45)" }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-6"
        >
          <p className="text-sm uppercase tracking-[0.3em] text-[#9B8E7E] mb-4 font-medium">Our Signature Drips</p>
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-light text-[#3D3A34] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Choose Your Infusion
          </h2>
          <p className="text-[#6B6761] text-lg max-w-2xl mx-auto mb-4">
            Every infusion is thoughtfully formulated and approved by a clinician certified by the Canadian Board of Aesthetic Medicine in IV nutrient therapy, delivering safe, regulated care focused on hydration, nutritional support, and everyday wellness.
          </p>
        </motion.div>

        {/* Age Disclaimer */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex items-center justify-center gap-2 mb-14"
        >
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-[#E8E4DE] shadow-sm">
            <AlertCircle className="w-4 h-4 text-[#9B8E7E] flex-shrink-0" />
            <span className="text-xs text-[#6B6761] font-medium">You must be 18 years of age or older to book our premium infusions.</span>
          </div>
        </motion.div>

        {/* Drip Selector Pills */}
        <div className="flex flex-wrap justify-center gap-3 mb-16">
          {drips.map((drip) => (
            <button
              key={drip.id}
              onClick={() => setActive(drip.id)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 border ${
                active === drip.id
                  ? "bg-[#3D3A34] text-white border-transparent shadow-md"
                  : "bg-white text-[#6B6761] border-[#E8E4DE] hover:border-[#9B8E7E] hover:text-[#3D3A34]"
              }`}
            >
              {drip.name.replace("Haven Wellness ", "")}
            </button>
          ))}
        </div>

        {/* Active Drip Display */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center"
          >
            {/* IV Bag Image */}
            <div className="relative flex justify-center">
              <div className="relative">
                <div
                  className="absolute inset-0 rounded-full blur-3xl scale-125"
                  style={{ background: `radial-gradient(ellipse at center, ${current.glowColor}, transparent 65%)`, opacity: 0.8 }}
                />
                <div
                  className="absolute inset-0 rounded-full blur-2xl scale-110"
                  style={{ background: `radial-gradient(ellipse at center, ${current.glowColor2}, transparent 60%)`, opacity: 0.6 }}
                />
                <motion.img
                  src={current.image}
                  alt={current.name}
                  className="relative w-[260px] sm:w-[320px] lg:w-[380px] h-auto object-contain"
                  animate={{ y: [0, -12, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  loading="lazy"
                />
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 }}
                  className="absolute -bottom-4 -right-4 sm:-right-6 px-4 py-2 rounded-2xl bg-[#3D3A34] text-white text-xs font-medium uppercase tracking-wider shadow-lg"
                >
                  {active === "iron" ? "Haven IV Iron" : "Haven Wellness Health"}
                </motion.div>
              </div>
            </div>

            {/* Drip Info */}
            <div>
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border ${current.accentBorder} mb-6 shadow-sm`}>
                <current.icon className={`w-4 h-4 ${current.iconColor}`} />
                <span className={`text-xs uppercase tracking-[0.2em] font-medium ${current.accentText}`}>{current.tagline}</span>
              </div>

              <h3 className="text-3xl sm:text-4xl lg:text-5xl font-light text-[#3D3A34] mb-6 leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {current.name}
              </h3>

              <p className="text-[#6B6761] text-lg leading-relaxed mb-8">
                {current.description}
              </p>

              {/* Benefits */}
              <div className="mb-8">
                <p className="text-[#9B8E7E] text-xs uppercase tracking-[0.2em] mb-4">Key Benefits</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {current.benefits.map((benefit) => (
                    <div key={benefit} className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#9B8E7E] flex-shrink-0" />
                      <span className="text-[#3D3A34] text-sm">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Ingredients accordion */}
              <button
                onClick={() => setExpandedBenefit(expandedBenefit === active ? null : active)}
                className={`w-full flex items-center justify-between p-4 rounded-2xl border ${current.accentBorder} bg-white mb-6 hover:bg-[#F0EDE8] transition-colors shadow-sm`}
              >
                <span className="text-[#6B6761] text-sm">View Ingredients</span>
                <ChevronDown className={`w-4 h-4 text-[#9B8E7E] transition-transform ${expandedBenefit === active ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {expandedBenefit === active && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden mb-6"
                  >
                    <div className={`p-4 rounded-2xl ${current.accentBg} border ${current.accentBorder}`}>
                      <p className={`text-sm ${current.accentText} font-medium leading-relaxed`}>{current.ingredients}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Ideal for */}
              <p className="text-[#9B8E7E] text-sm italic mb-8 leading-relaxed">{current.ideal}</p>

              {/* Pricing */}
              <div className="inline-flex items-baseline gap-2 mb-8 px-6 py-3 bg-white border border-[#E8E4DE] rounded-2xl shadow-sm">
                <span className="text-4xl font-light text-[#3D3A34]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{current.price}</span>
                <span className="text-sm text-[#9B8E7E]">per session</span>
              </div>

              {/* CTA - only shown on Premium Blend, redirects to inquiry form */}
              {active === "premium" && (
                <div className="flex flex-col sm:flex-row gap-4">
                  <a href="#event-inquiry" className="px-8 py-4 rounded-full font-medium bg-[#9B8E7E] text-white hover:bg-[#8A7D6D] transition-all hover:shadow-lg inline-flex items-center justify-center gap-2">
                    Book IV
                  </a>
                </div>
              )}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* All drips mini grid */}
        <div className="mt-20 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {drips.map((drip, index) => (
            <motion.button
              key={drip.id}
              onClick={() => setActive(drip.id)}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.08 }}
              className={`relative p-4 rounded-2xl border text-left transition-all duration-300 group ${
                active === drip.id
                  ? "border-[#9B8E7E] bg-white shadow-md"
                  : "border-[#E8E4DE] bg-white/60 hover:bg-white hover:border-[#9B8E7E] hover:shadow-sm"
              }`}
            >
              <img
                src={drip.image}
                alt={drip.name}
                className="w-full h-24 object-contain mb-3"
                loading="lazy"
              />
              <p className="text-[#3D3A34] text-xs font-medium leading-tight">
                {drip.name.replace("Haven Wellness ", "")}
              </p>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  )
}

function EventInquiryForm() {
  const [eventType, setEventType] = useState("")
  const [otherSpecify, setOtherSpecify] = useState("")
  const [guestCount, setGuestCount] = useState("")
  const [email, setEmail] = useState("")
  const [name, setName] = useState("")
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const selectedEvent = eventOptions.find(o => o.value === eventType)
    const eventLabel = eventType === "other"
      ? `Other - ${otherSpecify}`
      : (selectedEvent ? selectedEvent.label : eventType)

    const subject = `IV Therapy Event Inquiry - ${name}`
    const body = [
      "New IV Therapy event inquiry from the website:",
      "",
      `Name: ${name}`,
      `Email: ${email}`,
      `Event Type: ${eventLabel}`,
      `Number of Guests: ${guestCount}`,
      "",
      "Please follow up within 24 hours.",
    ].join("\n")

    window.location.href = `mailto:${INQUIRY_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`

    setSubmitted(true)
  }

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white border border-[#E8E4DE] rounded-3xl p-10 text-center"
      >
        <div className="w-16 h-16 bg-[#F0EDE8] rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 className="w-8 h-8 text-[#9B8E7E]" />
        </div>
        <h3 className="text-2xl font-light text-[#3D3A34] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Inquiry Received
        </h3>
        <p className="text-[#6B6761] text-sm leading-relaxed max-w-xs mx-auto">
          Thank you! Your inquiry has been sent to our team at info@havenintegrativehealthconsulting.com. We will reach out within 24 hours to discuss your event details.
        </p>
      </motion.div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white border border-[#E8E4DE] rounded-3xl p-8 lg:p-10 space-y-6">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#9B8E7E] mb-1 font-medium">Your Name</p>
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          required
          placeholder="Full name"
          className="w-full px-4 py-3 rounded-xl border border-[#E8E4DE] bg-[#FAF8F5] text-[#3D3A34] text-sm placeholder-[#B0A99F] focus:outline-none focus:border-[#9B8E7E] transition-colors"
        />
      </div>

      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#9B8E7E] mb-1 font-medium">Email Address</p>
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
          placeholder="you@example.com"
          className="w-full px-4 py-3 rounded-xl border border-[#E8E4DE] bg-[#FAF8F5] text-[#3D3A34] text-sm placeholder-[#B0A99F] focus:outline-none focus:border-[#9B8E7E] transition-colors"
        />
      </div>

      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#9B8E7E] mb-1 font-medium">Event Type</p>
        <div className="relative">
          <select
            value={eventType}
            onChange={e => { setEventType(e.target.value); setOtherSpecify("") }}
            required
            className="w-full px-4 py-3 rounded-xl border border-[#E8E4DE] bg-[#FAF8F5] text-[#3D3A34] text-sm focus:outline-none focus:border-[#9B8E7E] transition-colors appearance-none pr-10"
          >
            <option value="" disabled>Select your event type</option>
            {eventOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9B8E7E] pointer-events-none" />
        </div>
      </div>

      {/* Show "please specify" field if Other is selected */}
      <AnimatePresence>
        {eventType === "other" && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <p className="text-xs uppercase tracking-[0.2em] text-[#9B8E7E] mb-1 font-medium">Please Specify</p>
            <input
              type="text"
              value={otherSpecify}
              onChange={e => setOtherSpecify(e.target.value)}
              required
              placeholder="Tell us about your event"
              className="w-full px-4 py-3 rounded-xl border border-[#E8E4DE] bg-[#FAF8F5] text-[#3D3A34] text-sm placeholder-[#B0A99F] focus:outline-none focus:border-[#9B8E7E] transition-colors"
            />
          </motion.div>
        )}
      </AnimatePresence>

      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#9B8E7E] mb-1 font-medium">Number of Guests</p>
        <input
          type="number"
          value={guestCount}
          onChange={e => setGuestCount(e.target.value)}
          required
          min={3}
          placeholder="Minimum 3 guests per event"
          className="w-full px-4 py-3 rounded-xl border border-[#E8E4DE] bg-[#FAF8F5] text-[#3D3A34] text-sm placeholder-[#B0A99F] focus:outline-none focus:border-[#9B8E7E] transition-colors"
        />
        <p className="text-xs text-[#9B8E7E] mt-1.5 ml-1">A minimum of 3 client bookings is required per event.</p>
      </div>

      <button
        type="submit"
        className="w-full py-4 bg-[#9B8E7E] text-white rounded-full font-medium hover:bg-[#8A7D6D] transition-all hover:shadow-lg inline-flex items-center justify-center gap-2"
      >
        <Send className="w-4 h-4" />
        Send Inquiry
      </button>
    </form>
  )
}

function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0)

  return (
    <section id="faq" className="py-14 sm:py-20 lg:py-32 bg-[#FAF8F5]">
      <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-12">
        <motion.div {...fadeUp} transition={{ duration: 0.8 }} className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E8E4DE] mb-6">
            <HelpCircle className="w-4 h-4 text-[#9B8E7E]" />
            <span className="text-xs uppercase tracking-[0.2em] text-[#9B8E7E] font-medium">Frequently Asked</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-6xl font-light text-[#3D3A34] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Your questions, answered
          </h2>
          <p className="text-lg text-[#6B6761] max-w-2xl mx-auto">
            Everything you need to know about our mobile IV nutrient therapy experience.
          </p>
        </motion.div>

        <div className="space-y-3">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index
            return (
              <motion.div
                key={faq.q}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: index * 0.04 }}
                className={`rounded-2xl border transition-colors ${
                  isOpen ? "bg-white border-[#9B8E7E]" : "bg-white border-[#E8E4DE] hover:border-[#C9A96E]/60"
                }`}
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  className="w-full flex items-center justify-between gap-4 p-5 lg:p-6 text-left"
                >
                  <span className="text-base lg:text-lg font-medium text-[#3D3A34] flex-1">
                    {faq.q}
                  </span>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                    isOpen ? "bg-[#9B8E7E] text-white rotate-180" : "bg-[#F0EDE8] text-[#9B8E7E]"
                  }`}>
                    <ChevronDown className="w-4 h-4" />
                  </div>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 lg:px-6 pb-6 pt-0 text-[#6B6761] leading-relaxed text-[15px]">
                        {faq.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )
          })}
        </div>

        {/* Still have questions CTA */}
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.8 }}
          className="mt-14 p-8 lg:p-10 rounded-3xl bg-gradient-to-br from-[#3D3A34] to-[#1F1D1A] text-center"
        >
          <h3 className="text-2xl lg:text-3xl font-light text-white mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Still have questions?
          </h3>
          <p className="text-white/70 mb-6 max-w-md mx-auto text-[15px]">
            Our clinical team is here to help. Send us an inquiry and we will get back to you within 24 hours.
          </p>
          <a
            href="#event-inquiry"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#C9A96E] text-[#1F1D1A] rounded-full font-medium text-sm tracking-wide hover:bg-[#D4B57A] transition-colors"
          >
            <Send className="w-4 h-4" />
            Send an Inquiry
          </a>
        </motion.div>
      </div>
    </section>
  )
}

function WellnessHealth() {
  useSeoMeta({
    title: "Mobile IV Therapy Near Me | Vancouver & Lower Mainland",
    description: "Mobile IV therapy in Vancouver, Burnaby, Coquitlam & Surrey. Licensed nurses deliver IV hydration, vitamin drips & iron therapy at home, office or hotel. Book online.",
    ogTitle: "Mobile IV Therapy Vancouver | Haven Wellness Health",
    ogDescription: "In-home, corporate, and event IV drips, hydration, athlete recovery, and beauty blends delivered across Vancouver.",
    ogImage: "https://static.lumi.new/cd/cd567d0e238b43d766e1e85325f180c2.webp",
    ogUrl: "https://www.havenintegrativehealthconsulting.com/havenwellnesshealth",
    twitterCard: "summary_large_image",
    twitterImage: "https://static.lumi.new/cd/cd567d0e238b43d766e1e85325f180c2.webp",
  })

  useHead({
    link: [
      { rel: "canonical", href: "https://www.havenintegrativehealthconsulting.com/havenwellnesshealth" },
    ],
    meta: [
      {
        name: "keywords",
        content: "mobile IV therapy near me, mobile IV Vancouver, IV therapy Burnaby, IV hydration Coquitlam, at-home IV drip Vancouver, mobile IV nurse near me, mobile IV therapy Vancouver, mobile IV hydration, IV hydration Vancouver, vitamin therapy Vancouver, in-home IV therapy, IV nutrient therapy Vancouver, mobile IV drip Vancouver, in-clinic IV therapy, hangover recovery IV therapy, post-workout recovery IV, athlete recovery IV drip, corporate IV therapy, office IV therapy, event IV therapy, wedding IV hydration, bachelorette IV therapy, immune boost IV therapy, energy boost IV drip, skin hydration IV, dehydration IV therapy, IV therapy Burnaby, IV therapy Coquitlam, IV therapy Delta, IV therapy Langley, IV therapy New Westminster, IV therapy Richmond, IV therapy Surrey, IV therapy West Vancouver, Metro Vancouver mobile IV therapy, Greater Vancouver IV therapy, registered nurse IV therapy, IV drip treatments, mobile IV drip near me, IV therapy near me, we come to you IV therapy, mobile IV bar Vancouver, IV therapy for groups, IV therapy for events, corporate wellness event IV, VIP event IV therapy, bridal party IV therapy, vitamin injections Vancouver, vitamin B12 injection, B12 shot Vancouver, vitamin D injection, Biotin injection, Vitamin B Complex injection, Glutathione injection, Glutathione shot, Myers Cocktail IV Vancouver, recovery IV drip, beauty IV drip, headache relief IV therapy, migraine relief IV drip, stomach flu IV therapy, cleanse IV drip, all-inclusive IV drip, iron infusion therapy Vancouver, IV therapy membership, vitamin drip membership, physician-supervised IV therapy, IV therapy administered by registered nurse",
      },
    ],
    script: [
      {
        type: "application/ld+json",
        innerHTML: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalBusiness",
          name: "Haven Wellness Health - Mobile IV Therapy",
          image: "https://static.lumi.new/cd/cd567d0e238b43d766e1e85325f180c2.webp",
          url: "https://www.havenintegrativehealthconsulting.com/havenwellnesshealth",
          telephone: "778-898-3419",
          address: {
            "@type": "PostalAddress",
            addressLocality: "Vancouver",
            addressRegion: "BC",
            addressCountry: "CA",
          },
          areaServed: ["Vancouver", "Burnaby", "Coquitlam", "Delta", "Langley", "New Westminster", "North Vancouver", "Richmond", "Surrey", "West Vancouver", "White Rock", "Metro Vancouver", "Lower Mainland"],
          priceRange: "$300-$400",
          makesOffer: [
            {
              "@type": "Offer",
              itemOffered: {
                "@type": "Service",
                name: "Mobile IV Therapy Near Me",
                areaServed: "Metro Vancouver",
              },
            },
            {
              "@type": "Offer",
              itemOffered: {
                "@type": "Service",
                name: "Corporate Event IV Therapy",
                description: "On-site mobile IV hydration and vitamin therapy for corporate wellness events, minimum 3 guests",
              },
            },
            {
              "@type": "Offer",
              itemOffered: {
                "@type": "Service",
                name: "Wedding & Private Party IV Therapy",
                description: "In-home mobile IV drips for weddings, bridal parties, and private group bookings",
              },
            },
            {
              "@type": "Offer",
              itemOffered: {
                "@type": "Service",
                name: "Athletic Recovery IV Drip",
                description: "Mobile IV therapy for athlete hydration and post-performance recovery",
              },
            },
            {
              "@type": "Offer",
              itemOffered: {
                "@type": "Service",
                name: "Beauty & Skin-Supportive IV Drip",
                description: "Vitamin infusion for skin health delivered at home, hotel, or event",
              },
            },
          ],
        }),
      },
    ],
  })

  return (
    <div id="iv-top" className="min-h-screen bg-[#FAF8F5]">
      {/* Dark Navigation - Promethean-inspired with all service pillars */}
      <HavenNav />
      <IVSubNav />

      <main>
      {/* Top Regulatory Disclaimer */}
      <div className="pt-[124px] bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12 pt-4">
          <div className="flex items-start gap-3 p-4 rounded-2xl bg-white border border-[#E8E4DE]">
            <AlertCircle className="w-4 h-4 text-[#9B8E7E] flex-shrink-0 mt-0.5" />
            <p className="text-xs sm:text-sm text-[#6B6761] leading-relaxed">
              Our IV nutrient therapies are provided for nutritional supplementation and supportive wellness purposes only. These services are not intended to diagnose, treat, cure, or prevent any disease, nor are they offered as medical aesthetic procedures or cosmetic treatments.
            </p>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="pt-12 pb-20 lg:pt-16 lg:pb-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#FAF8F5] via-[#F5F0EB] to-[#FAF8F5]" />
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-[#E8E4DE] mb-8">
                <Droplets className="w-4 h-4 text-[#9B8E7E]" />
                <span className="text-xs uppercase tracking-[0.2em] text-[#9B8E7E] font-medium">Haven Wellness Health</span>
              </div>
              <h1 className="text-3xl sm:text-5xl lg:text-7xl font-light mb-6 text-[#3D3A34] leading-[1.1]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Your Trusted Provider of Mobile IV Therapy in Vancouver
              </h1>
              <p className="text-xl text-[#6B6761] mb-10 font-light leading-relaxed max-w-lg">
                We bring professional IV hydration and vitamin therapy directly to your home, office, or hotel, personalized wellness infusions delivered to you, at home, events, and beyond.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href={JANE_BOOK_NOW} target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-[#9B8E7E] text-white rounded-full font-medium hover:bg-[#8A7D6D] transition-all hover:shadow-lg inline-flex items-center justify-center gap-2">
                  Book Now
                </a>
                <a href="#event-inquiry" className="px-8 py-4 bg-white text-[#3D3A34] rounded-full font-medium border border-[#E8E4DE] hover:border-[#9B8E7E] transition-all inline-flex items-center justify-center">
                  Book an Event
                </a>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="rounded-3xl overflow-hidden">
                <div className="grid grid-cols-2 gap-3 h-[300px] sm:h-[400px] lg:h-[520px]">
                  <img
                    src="https://static.lumi.new/cd/cd567d0e238b43d766e1e85325f180c2.webp"
                    alt="Woman receiving IV therapy"
                    className="w-full h-full object-cover rounded-2xl"
                  />
                  <img
                    src="https://static.lumi.new/dd/dd9668ee4f27c5b171875acda3aba6c4.webp"
                    alt="Haven Wellness Health group IV session"
                    className="w-full h-full object-cover rounded-2xl"
                  />
                </div>
              </div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="absolute -bottom-4 sm:-bottom-6 left-2 sm:-left-4 lg:-left-8 bg-white p-4 sm:p-5 rounded-xl sm:rounded-2xl shadow-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#F0EDE8] rounded-xl flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-[#9B8E7E]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#3D3A34]">We come to you</p>
                    <p className="text-xs text-[#6B6761]">Home, office, or event</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Scrolling image strip above drips */}
      <div className="w-full overflow-hidden bg-[#FAF8F5] py-6">
        <div
          className="flex gap-4"
          style={{
            animation: "havenScroll 28s linear infinite",
            width: "max-content",
          }}
        >
          {[
            "https://static.lumi.new/98/98168eb46dbc1a556d754c8b8bcd2177.webp",
            "https://static.lumi.new/dd/dd9668ee4f27c5b171875acda3aba6c4.webp",
            "https://static.lumi.new/c8/c882f4620af79f056aaf2f3e923a5876.webp",
            "https://static.lumi.new/ed/ed91f3f1a0f5487df0f74bae6da8a07f.webp",
            "https://static.lumi.new/81/81fcc45f82b12e53911182438ff8d234.webp",
            "https://static.lumi.new/98/98168eb46dbc1a556d754c8b8bcd2177.webp",
            "https://static.lumi.new/dd/dd9668ee4f27c5b171875acda3aba6c4.webp",
            "https://static.lumi.new/c8/c882f4620af79f056aaf2f3e923a5876.webp",
            "https://static.lumi.new/ed/ed91f3f1a0f5487df0f74bae6da8a07f.webp",
            "https://static.lumi.new/81/81fcc45f82b12e53911182438ff8d234.webp",
          ].map((src, i) => (
            <div
              key={i}
              className="flex-shrink-0 rounded-2xl overflow-hidden"
              style={{ width: "280px", height: "340px" }}
            >
              <img
                src={src}
                alt="Haven IV therapy event"
                className="w-full h-full object-cover pointer-events-none select-none"
                draggable={false}
                loading="lazy"
              />
            </div>
          ))}
        </div>
      </div>

      {/* IV Drip Showcase */}
      <IVDripShowcase />

      {/* Signature IV Therapy Services */}
      <section id="add-ons" className="py-14 sm:py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12">
          <motion.div {...fadeUp} transition={{ duration: 0.8 }} className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.25em] text-[#9B8E7E] mb-4 font-medium">What We Offer</p>
            <h2 className="text-3xl sm:text-4xl lg:text-6xl font-light mb-6 text-[#3D3A34]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Signature IV Therapy
            </h2>
            <p className="text-lg text-[#6B6761] max-w-2xl mx-auto">
              Clinically crafted infusions designed to restore, protect, and elevate your well-being.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group p-8 rounded-3xl bg-[#FAF8F5] hover:bg-[#F0EDE8] transition-all cursor-pointer"
              >
                <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#9B8E7E] transition-colors shadow-sm">
                  <service.icon className="w-7 h-7 text-[#9B8E7E] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-serif mb-3 text-[#3D3A34]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {service.title}
                </h3>
                <p className="text-sm text-[#6B6761] leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Premium Blend + Add-Ons combined */}
          <div className="mt-20">
            <motion.div
              {...fadeUp}
              transition={{ duration: 0.8 }}
              className="bg-gradient-to-br from-[#F0EDE8] to-[#FAF8F5] p-10 lg:p-14 rounded-3xl max-w-4xl mx-auto"
            >
              <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-6 shadow-sm">
                <Droplets className="w-7 h-7 text-[#9B8E7E]" />
              </div>
              <p className="text-sm uppercase tracking-[0.2em] text-[#9B8E7E] mb-4 font-medium">Enhance Your Premium Blend</p>
              <h3 className="text-3xl lg:text-4xl font-light mb-4 text-[#3D3A34]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Premium Add-Ons
              </h3>
              <p className="text-[#6B6761] leading-relaxed text-lg mb-8">
                Personalize your Premium Blend with clinician-approved add-ons designed to deepen hydration, support recovery, and enhance your wellness experience.
              </p>
              <div className="space-y-4">
                {addOns.map((addon) => (
                  <div key={addon.name} className="flex items-start justify-between gap-4 p-5 rounded-2xl bg-white border border-[#E8E4DE]">
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-[#9B8E7E] rounded-full mt-2 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-[#3D3A34]">{addon.name}</p>
                        <p className="text-xs text-[#6B6761] mt-0.5">{addon.description}</p>
                      </div>
                    </div>
                    <span className="text-sm font-medium text-[#9B8E7E] whitespace-nowrap flex-shrink-0">{addon.price}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>



      {/* Events Section */}
      <section id="event-inquiry" className="py-14 sm:py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <p className="text-sm uppercase tracking-[0.25em] text-[#9B8E7E] mb-4 font-medium">Special Occasions</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light mb-6 text-[#3D3A34]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                IV Therapy for Events
              </h2>
              <p className="text-lg text-[#6B6761] mb-8 leading-relaxed">
                Bring wellness to your most important moments. From bridal prep to office retreats and fitness studio sessions, we deliver a thoughtful hydration and wellness experience your guests will remember.
              </p>

              {/* Event type cards */}
              <div className="space-y-4 mb-8">
                {eventOptions.filter(o => o.value !== "other").map((opt) => (
                  <div key={opt.value} className="flex items-center gap-4 p-4 rounded-2xl bg-[#FAF8F5] border border-[#E8E4DE]">
                    <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm flex-shrink-0">
                      <opt.icon className="w-5 h-5 text-[#9B8E7E]" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#3D3A34]">{opt.label}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-3 p-4 rounded-2xl bg-[#F0EDE8] border border-[#E8E4DE]">
                <AlertCircle className="w-5 h-5 text-[#9B8E7E] flex-shrink-0" />
                <p className="text-sm text-[#6B6761]">A minimum of <span className="font-medium text-[#3D3A34]">3 client bookings</span> is required per event. All ages must be 18+ to receive infusions.</p>
              </div>

            </motion.div>

            {/* Inquiry Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="mb-6">
                <h3 className="text-2xl font-light text-[#3D3A34] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Send Us Your Inquiry
                </h3>
                <p className="text-sm text-[#6B6761]">Fill in the details below and our team will be in touch within 24 hours.</p>
              </div>
              <EventInquiryForm />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Event & Audience Categories - Vancouver Mobile IV Therapy */}
      <section id="iv-occasions" className="py-14 sm:py-20 lg:py-28 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12">
          <motion.div {...fadeUp} transition={{ duration: 0.8 }} className="text-center mb-14">
            <p className="text-sm uppercase tracking-[0.25em] text-[#9B8E7E] mb-4 font-medium">Mobile IV Bar Vancouver</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div {...fadeUp} transition={{ duration: 0.6 }}>
              <h2 className="text-xl font-light mb-3 text-[#3D3A34]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Corporate Wellness Events Vancouver</h2>
              <p className="text-[#6B6761] leading-relaxed text-sm">A corporate wellness event IV experience brought directly to your Vancouver office through our mobile IV bar Vancouver service and private group IV booking options.</p>
            </motion.div>
            <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.1 }}>
              <h2 className="text-xl font-light mb-3 text-[#3D3A34]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Weddings & Private Parties Vancouver</h2>
              <p className="text-[#6B6761] leading-relaxed text-sm">From bridal prep to a wedding party hydration IV session, our in-home IV drip Vancouver service keeps every private party glowing.</p>
            </motion.div>
            <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.2 }}>
              <h2 className="text-xl font-light mb-3 text-[#3D3A34]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Athlete Recovery Drips Vancouver</h2>
              <p className="text-[#6B6761] leading-relaxed text-sm">Our athlete recovery IV drip supports hydration and replenishment after training, delivered wherever you train across Vancouver.</p>
            </motion.div>
            <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.3 }}>
              <h2 className="text-xl font-light mb-3 text-[#3D3A34]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Beauty & Skin Drips Vancouver</h2>
              <p className="text-[#6B6761] leading-relaxed text-sm">Our beauty IV drip Vancouver blend nourishes skin from within, available as an in-home IV drip Vancouver session or private group IV booking.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Areas We Serve - Mobile IV Therapy Near Me */}
      <section id="areas-we-serve" className="py-14 sm:py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12">
          <motion.div {...fadeUp} transition={{ duration: 0.8 }} className="text-center mb-14">
            <p className="text-sm uppercase tracking-[0.25em] text-[#9B8E7E] mb-4 font-medium">Areas We Serve</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light mb-6 text-[#3D3A34]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Mobile IV Therapy Near Me
            </h2>
            <p className="text-lg text-[#6B6761] max-w-2xl mx-auto">
              Haven Wellness Health delivers mobile IV therapy and IV hydration at home, office, or hotel throughout Vancouver and the Lower Mainland.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div {...fadeUp} transition={{ duration: 0.6 }}>
              <h3 className="text-xl font-light mb-3 text-[#3D3A34]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Mobile IV Therapy in Vancouver</h3>
              <p className="text-[#6B6761] leading-relaxed text-sm">IV hydration at home Vancouver, delivered by registered nurses directly to your door, office, or hotel.</p>
            </motion.div>
            <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.1 }}>
              <h3 className="text-xl font-light mb-3 text-[#3D3A34]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Mobile IV Therapy in Burnaby</h3>
              <p className="text-[#6B6761] leading-relaxed text-sm">On-site IV drip Burnaby bookings for individuals, events, and corporate wellness days.</p>
            </motion.div>
            <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.2 }}>
              <h3 className="text-xl font-light mb-3 text-[#3D3A34]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Mobile IV Therapy in Coquitlam</h3>
              <p className="text-[#6B6761] leading-relaxed text-sm">On-site IV drip Coquitlam service bringing IV hydration and vitamin injections near me Vancouver-area clients trust.</p>
            </motion.div>
            <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.3 }}>
              <h3 className="text-xl font-light mb-3 text-[#3D3A34]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Mobile IV Therapy in Surrey</h3>
              <p className="text-[#6B6761] leading-relaxed text-sm">On-site IV drip Surrey appointments, including IV iron therapy Vancouver-area clients can also access.</p>
            </motion.div>
          </div>
        </div>
      </section>
      <section className="py-14 sm:py-20 lg:py-32 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-sm uppercase tracking-[0.3em] text-[#9B8E7E] mb-4 font-medium">Client Stories</p>
            <h2 className="text-3xl sm:text-5xl lg:text-6xl font-light text-[#3D3A34] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              What Our Clients Are Saying
            </h2>
            <p className="text-[#6B6761] text-lg max-w-xl mx-auto">Real experiences from real people. Recent reviews from our community.</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                name: "Lizzie Jha",
                tag: "Bridal Event",
                tagIcon: Flower2,
                review: "I booked Haven for my bridal party and bachelorette at our wedding house, and it was honestly one of the best decisions I made for the whole weekend. The team was so professional and warm. We all felt completely at ease, and the experience felt so elevated. Every single one of us was glowing after. I cannot recommend this enough for any bride.",

              },
              {
                name: "Scott Reid",
                tag: "Athletic Recovery",
                tagIcon: Battery,
                review: "After a brutal HIIT session with the guys, we booked the Athletic Support drip and the difference was night and day. The soreness cleared up faster than anything we have tried before and we were all back on our feet within hours. The RN was knowledgeable, professional, and made the whole experience seamless. Will be making this a regular part of our recovery routine.",

              },
              {
                name: "Hailey Dye",
                tag: "Corporate Reset",
                tagIcon: Briefcase,
                review: "Our team had just come out of the most intense quarter of the year and we needed a real reset. Haven brought the experience right to our office and it was incredible. Everyone was so depleted going in and completely recharged coming out. The pharmacist-led intake was thorough and made us feel genuinely cared for, not just like clients checking a box. This is corporate wellness done right.",

              },
            ].map((t, index) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.12 }}
                className="bg-white border border-[#E8E4DE] rounded-3xl p-8 flex flex-col gap-6 hover:shadow-lg transition-all"
              >
                {/* 5 stars */}
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-4 h-4" viewBox="0 0 20 20" fill="#C9A96E">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>

                {/* Quote */}
                <p className="text-[#6B6761] text-sm leading-relaxed flex-1 italic">
                  &ldquo;{t.review}&rdquo;
                </p>

                {/* Author */}
                <div className="flex items-center justify-between pt-4 border-t border-[#F0EDE8]">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#F0EDE8] flex items-center justify-center">
                      <span className="text-[#9B8E7E] text-sm font-medium">{t.name.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="text-[#3D3A34] text-sm font-medium">{t.name}</p>

                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FAF8F5] border border-[#E8E4DE]">
                    <t.tagIcon className="w-3 h-3 text-[#9B8E7E]" />
                    <span className="text-[#9B8E7E] text-xs font-medium">{t.tag}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-14 sm:py-20 lg:py-28 bg-[#F0EDE8]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12">
          <motion.div {...fadeUp} transition={{ duration: 0.8 }} className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light mb-4 text-[#3D3A34]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Who it's for
            </h2>
            <p className="text-lg text-[#6B6761]">If this sounds like you, we're ready when you are.</p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whoItsFor.map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-3xl text-center hover:shadow-lg transition-all"
              >
                <div className="w-14 h-14 bg-[#FAF8F5] rounded-2xl flex items-center justify-center mx-auto mb-5">
                  <item.icon className="w-7 h-7 text-[#9B8E7E]" />
                </div>
                <h4 className="text-lg font-serif mb-2 text-[#3D3A34]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {item.label}
                </h4>
                <p className="text-sm text-[#6B6761]">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-14 sm:py-20 lg:py-32 bg-[#FAF8F5]">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-12">
          <motion.div {...fadeUp} transition={{ duration: 0.8 }} className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.25em] text-[#9B8E7E] mb-4 font-medium">Simple & Seamless</p>
            <h2 className="text-3xl sm:text-4xl lg:text-6xl font-light mb-6 text-[#3D3A34]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              How it works
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-6xl font-light text-[#E8E4DE] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {step.number}
                </div>
                <h3 className="text-2xl font-light mb-4 text-[#3D3A34]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {step.title}
                </h3>
                <p className="text-[#6B6761] leading-relaxed">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Medical Credibility */}
      <section className="py-14 sm:py-20 lg:py-32 bg-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-12">
          <motion.div {...fadeUp} transition={{ duration: 0.8 }} className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.25em] text-[#9B8E7E] mb-4 font-medium">Your Safety, Our Priority</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light mb-6 text-[#3D3A34]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Regulated care. Premium delivery.
            </h2>
            <p className="text-lg text-[#6B6761] max-w-2xl mx-auto">
              Every infusion begins with an initial consultation with our pharmacist and nurse practitioner, followed by administration by a registered nurse specialized in IV insertion and patient monitoring, so you receive care that is as safe as it is supportive.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: "Pharmacist-Led Consultation",
                description: "Your care begins with a one-on-one consultation with our pharmacist, certified by the Canadian Board of Aesthetic Medicine in IV nutrient therapy. They carefully review all of your previous medications, supplements, and complete medical history to ensure your infusion is safe, appropriate, and thoughtfully designed around your personalized wellness protocol.",
              },
              {
                icon: Users,
                title: "Nurse Practitioner Oversight",
                description: "Our nurse practitioner co-signs every care plan, ensuring your infusion is medically appropriate, regulated, and aligned with current evidence-based standards of practice.",
              },
              {
                icon: CheckCircle2,
                title: "Registered Nurse Administration",
                description: "All infusions are administered by a registered nurse specialized in IV insertion and ongoing patient monitoring, delivering a safe, professional, and deeply comfortable experience.",
              },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-[#FAF8F5] p-8 rounded-3xl text-center"
              >
                <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm">
                  <item.icon className="w-7 h-7 text-[#9B8E7E]" />
                </div>
                <h4 className="text-xl font-serif mb-3 text-[#3D3A34]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {item.title}
                </h4>
                <p className="text-sm text-[#6B6761] leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection />

      {/* Final CTA */}
      <section className="py-14 sm:py-20 lg:py-32 bg-[#3D3A34] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-12 text-center relative">
          <motion.div {...fadeUp} transition={{ duration: 0.8 }}>
            <h2 className="text-3xl sm:text-4xl lg:text-6xl font-light mb-6 text-white leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Your wellness journey starts here
            </h2>
            <p className="text-xl text-white/80 mb-10 font-light max-w-2xl mx-auto">
              Book your consultation today and experience personalized IV therapy designed around your life.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#event-inquiry" className="px-10 py-5 bg-white text-[#3D3A34] rounded-full font-medium text-lg hover:shadow-2xl transition-all hover:scale-105 inline-flex items-center justify-center gap-2">
                <Calendar className="w-5 h-5" />
                Book IV Therapy
              </a>
              <a href="https://havenintegrativehealth.janeapp.com/login" target="_blank" rel="noopener noreferrer" className="px-10 py-5 bg-transparent text-white rounded-full font-medium text-lg border border-white/30 hover:border-white/60 transition-all inline-flex items-center justify-center">
                Start Consultation
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      </main>

      {/* Footer */}
      <footer className="bg-[#1F1D1A] text-white py-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 mb-10">
            <div className="col-span-2 md:col-span-1">
              <h4 className="text-2xl font-serif mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Haven</h4>
              <p className="text-white/60 text-sm leading-relaxed">Clinical expertise.<br/>Elevated wellness.</p>
            </div>
            <div>
              <h5 className="text-[11px] uppercase tracking-[0.2em] text-white/40 mb-4">Our Divisions</h5>
              <ul className="space-y-3 text-sm text-white/70">
                <li><Link to="/havenwellnesshealth" className="hover:text-white transition-colors">Haven Wellness Health</Link></li>
                <li><Link to="/menopausehealth" className="hover:text-white transition-colors">Haven Menopause Health</Link></li>
                <li><Link to="/travelhealth" className="hover:text-white transition-colors">Haven Travel Health</Link></li>
                <li><Link to="/acnehealth" className="hover:text-white transition-colors">Haven Acne Health</Link></li>
              </ul>
            </div>
            <div>
              <h5 className="text-[11px] uppercase tracking-[0.2em] text-white/40 mb-4">Company</h5>
              <ul className="space-y-3 text-sm text-white/70">
                <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
                <li><Link to="/team" className="hover:text-white transition-colors">Our Team</Link></li>
                <li><Link to="/blog/iv-therapy-myths" className="hover:text-white transition-colors">IV Therapy Myths</Link></li>
                <li><a href="#event-inquiry" className="hover:text-white transition-colors">Book Consultation</a></li>
                <li><a href="#faq" className="hover:text-white transition-colors">FAQs</a></li>
              </ul>
            </div>
            <div>
              <h5 className="text-[11px] uppercase tracking-[0.2em] text-white/40 mb-4">Legal</h5>
              <ul className="space-y-3 text-sm text-white/70">
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 text-center text-sm text-white/40">
            <p>&copy; 2024 Haven Integrative Health Consulting Inc. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default WellnessHealth
